import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { Fingerprint as FingerprintIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import {
  activerSurCetAppareil,
  biometrieDisponible,
  invitationRefusee,
  invitationReportee,
  marquerActive,
  membreActiveSurAppareil,
  messageErreurBiometrie,
  refuserInvitation,
  reporterInvitation
} from '../../services/biometrie.service';

// Après une connexion par mot de passe, propose une seule fois par session d'activer
// l'empreinte / le visage sur ce téléphone (si le téléphone sait le faire).
const BiometrieInvitation = () => {
  const { user, api } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    if (!user?.id) return undefined;
    if (membreActiveSurAppareil(user.id) || invitationRefusee(user.id) || invitationReportee()) return undefined;
    let actif = true;
    let minuteur;
    biometrieDisponible().then((ok) => {
      if (actif && ok) minuteur = setTimeout(() => setOuvert(true), 1500);
    });
    return () => { actif = false; clearTimeout(minuteur); };
  }, [user?.id]);

  const activer = async () => {
    setEnCours(true);
    setErreur('');
    try {
      await activerSurCetAppareil(api, user.id);
      setOuvert(false);
      enqueueSnackbar('Connexion par empreinte ou visage activée sur ce téléphone', { variant: 'success' });
    } catch (err) {
      if (err?.name === 'InvalidStateError') {
        // Ce téléphone est déjà enregistré (données du navigateur effacées) : rien à refaire.
        marquerActive(user.id);
        setOuvert(false);
        enqueueSnackbar('Ce téléphone est déjà activé', { variant: 'info' });
      } else {
        setErreur(messageErreurBiometrie(err));
      }
    } finally {
      setEnCours(false);
    }
  };

  const plusTard = () => { reporterInvitation(); setOuvert(false); };
  const nonMerci = () => { refuserInvitation(user.id); setOuvert(false); };

  return (
    <Dialog open={ouvert} onClose={plusTard} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
        Se connecter plus vite ?
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <FingerprintIcon sx={{ fontSize: 88, color: 'success.main' }} />
        </Box>
        <Typography variant="body1" align="center" sx={{ fontSize: '1.1rem', mb: 1 }}>
          La prochaine fois, connectez-vous avec <strong>votre empreinte ou votre visage</strong>,
          sans taper votre mot de passe.
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary">
          Votre empreinte reste sur votre téléphone : elle n'est jamais envoyée à l'association.
          Vous pourrez toujours utiliser votre mot de passe.
        </Typography>
        {erreur && <Alert severity="warning" sx={{ mt: 2 }}>{erreur}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          color="success"
          size="large"
          onClick={activer}
          disabled={enCours}
          sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
        >
          {enCours ? <CircularProgress size={24} color="inherit" /> : 'Activer maintenant'}
        </Button>
        <Button fullWidth onClick={plusTard} disabled={enCours} sx={{ textTransform: 'none' }}>
          Plus tard
        </Button>
        <Button fullWidth size="small" color="inherit" onClick={nonMerci} disabled={enCours}
          sx={{ textTransform: 'none', opacity: 0.7 }}>
          Ne plus me le proposer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BiometrieInvitation;
